/**
 * @file contains utils related to strings
 */

import { reportError } from '../services/reporting';

// RegEx to validate Mongo IDs against
const mongoIdRegex = /^[a-fA-F0-9]{24}$/;

/**
 * Capitalize the first letter of the passed string
 *
 * @param value The value to capitalize
 * @returns     The capitalized string
 */
export const capitalize = (value: string): string => {
  if (value.length < 2) {
    return value.toUpperCase();
  }

  return value.charAt(0).toUpperCase() + value.slice(1);
};

/**
 * Validates a string to see if it matches the mongoId format
 * note: This is not 100% check if something is an ID, just if something looks like a mongoId
 *
 * @param idToCheck Id to check
 * @returns         Whether the argument passed in is a valid mongo id or not
 */
export const validateMongoId = (
  idToCheck: string | null | undefined,
): boolean => {
  if (idToCheck === null || idToCheck === undefined) {
    reportError('MongoID not passed');
    return false;
  }

  const isValidId = mongoIdRegex.test(idToCheck);

  if (isValidId === false) {
    reportError('Invalid MongoID: ' + idToCheck);
  }

  return isValidId;
};

/**
 * Trim whitespace and truncate string to maximum length
 * (if it exceeds it, append '...')
 *
 * @param maxTextLength The maximum text length
 * @param originalText  The full text
 * @returns             The truncated text
 */
export const truncateText = (
  maxTextLength: number,
  originalText?: string | null,
) => {
  if (originalText === null || originalText === undefined) {
    return '';
  }

  if (maxTextLength < 0) {
    reportError('truncateText: negative length: ' + maxTextLength);
    return originalText;
  }

  const trimmedText = originalText.trim();

  return trimmedText.length > maxTextLength
    ? trimmedText.substring(0, maxTextLength) + '...'
    : trimmedText;
};
